import React, { useEffect, useState } from "react";
import { Avatar, Box, Flex, Text, useColorModeValue, Tooltip, Badge } from "@chakra-ui/react";
import { TypingIndicator } from "screens/thread/components/cells/components";
import { getDueSequentialQueueWorkflows } from "api/workflowSchedules";
import type { WorkflowSchedule } from "types/workflow/workflowSchedule";
import moment from "moment";
import { useUsersByIds } from "hooks/useUsers";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import capitalize from "lodash/capitalize";
import { formatDistanceToNow } from "date-fns";

const WorkflowSchedulesAdmin = () => {
  const [workflowSchedules, setWorkflowSchedules] = useState<WorkflowSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const titleColor = useColorModeValue("charli.lightGray", "gray.500");
  const bgColor = useColorModeValue("gray.200", "gray.700");
  const hoverBgColor = useColorModeValue("gray.50", "gray.600");
  const userIds = Array.from(new Set(workflowSchedules.map((schedule) => schedule.userId)));
  const { users } = useUsersByIds(userIds);
  console.log(workflowSchedules);

  useEffect(() => {
    const fetchWorkflowSchedules = async () => {
      try {
        setIsLoading(true);
        const data = await getDueSequentialQueueWorkflows();

        setWorkflowSchedules(data);
      } catch (err) {
        console.error("Error fetching workflow schedules:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkflowSchedules();
  }, []);

  if (isLoading) {
    return (
      <Flex height="200px" align="center" justify="center">
        <TypingIndicator />
      </Flex>
    );
  }

  return (
    <Box>
      {/* Table header */}
      <Box fontSize="small" borderBottomColor={bgColor} borderBottom="2px solid" mb={1} color={titleColor} display="flex" p={2}>
        <Box width="40px">User</Box>
        <Box width="100px">Status</Box>
        <Box width="300px">ID</Box>
        <Box width="300px">Intent</Box>
        <Box flex={1}>Entities</Box>
        <Box width="220px">Creation Date</Box>
      </Box>
      {/* Table rows */}
      {workflowSchedules.map((schedule) => (
        <Flex
          key={schedule.id}
          p={2}
          alignItems="top"
          _hover={{ bgColor: hoverBgColor }}
          borderBottom="1px solid"
          borderBottomColor={bgColor}>
          <Box width="40px" display="flex" alignItems="top">
            {users[schedule.userId] && (
              <Tooltip label={users[schedule.userId].fullName} placement="right">
                <Avatar size="xs" name={users[schedule.userId].fullName} src={users[schedule.userId]?.avatarUrl || undefined} mr={2} />
              </Tooltip>
            )}
          </Box>
          <Box width="100px">
            <Badge colorScheme={schedule.status === "in_progress" ? "green" : "orange"}>
              {capitalize(schedule.status).split("_").join(" ")}
            </Badge>
          </Box>
          <Box width="300px">
            <TextOverflowTooltip breakWord={false} label={schedule.id} />
          </Box>
          <Box width="300px">
            <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {schedule.intent}
            </Text>
          </Box>
          <Box flex={1}>
            <Box fontSize="sm">
              {schedule.entities.map((entity, index) => (
                <Box key={index} mb={1}>
                  <Text as="span" color="blue.500">
                    {entity.name}
                  </Text>
                  <Text as="span" mx={1}>
                    :
                  </Text>
                  <Text as="span" color="orange.500">
                    "{entity.value}"
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
          <Box width="220px" display="flex" alignItems="top">
            {users[schedule.lastUpdatedByUserId] && (
              <Tooltip label={users[schedule.lastUpdatedByUserId].fullName} placement="top">
                <Avatar
                  size="xs"
                  name={users[schedule.lastUpdatedByUserId].fullName}
                  src={users[schedule.lastUpdatedByUserId]?.avatarUrl || undefined}
                  mr={2}
                />
              </Tooltip>
            )}
            <Tooltip label={moment(schedule.creationDate).format("YYYY-MM-DD HH:mm:ss")} placement="top">
              <Text fontSize="sm" color={titleColor} mr={2}>
                {formatDistanceToNow(new Date(schedule.creationDate), {
                  addSuffix: true,
                  includeSeconds: false,
                })}
              </Text>
            </Tooltip>
          </Box>
        </Flex>
      ))}
      {workflowSchedules.length === 0 && !isLoading && (
        <Box textAlign="center" py={8}>
          <Text fontSize={"md"} color={titleColor}>
            No workflow schedules found.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export { WorkflowSchedulesAdmin };
export default WorkflowSchedulesAdmin;
